import {
  buildTournamentDayMatchIndex,
  clearMatchExplicitEndTimes,
  getEffectiveDayBounds,
  getMatchDurationFromConstraints,
  getTournamentDaySessionEndMs,
  matchBelongsToTournamentDay,
  rebuildEntireSchedule,
  resolveTournamentDayKeyForMatch,
  type TournamentDayMatchIndex,
} from '../../pages/tourDetails/Schedule/components/schedule.helpers';
import type { Class } from '../../types/tournament';
import {
  IFreeSlotItem,
  IMatchScheduleItem,
  IScheduleConstraints,
  IScheduleDayConstraints,
  ITournamentSchedule,
} from '../../types/schedule';
import {
  detectScheduleConflicts,
  type ScheduleConflict,
} from './scheduleConflictDetector';
import { flattenScheduledMatches, MS_PER_MINUTE, wouldOverlap } from './scheduleOverlap';
import { MAX_SCHEDULE_BULK_SELECTION } from './scheduleSelectionConstants';

export type BulkAdjustOps = {
  shiftMinutes?: number;
  shiftCourtColumns?: number;
  setDurationMinutes?: number;
};

export type BulkSkipReason =
  | 'cap'
  | 'cross_day'
  | 'out_of_courts'
  | 'out_of_day_bounds'
  | 'not_scheduled';

export type BulkTransformResult = {
  scheduledMatches: IMatchScheduleItem[];
  unscheduledMatches: IMatchScheduleItem[];
  skipped: { matchId: number; reason: BulkSkipReason }[];
  warnings: ScheduleConflict[];
  courtOverlaps: { matchId: number; messageKey: string }[];
  rebuiltSchedule?: ITournamentSchedule;
  stats: {
    appliedCount: number;
    skippedCount: number;
  };
};

export type SimulateBulkTransformMode = 'preview' | 'apply';

export type SimulateBulkTransformParams = {
  schedule: ITournamentSchedule;
  unscheduledMatches: IMatchScheduleItem[];
  targetMatchIds: number[];
  ops: BulkAdjustOps;
  constraints: IScheduleConstraints;
  timeZone: string;
  classIdToObject?: Record<string, Class>;
  mode?: SimulateBulkTransformMode;
};

type DayBoundsCache = {
  effectiveStartMs: number;
  sessionEndMs: number;
};

type BulkTransformContext = {
  dayIndex: TournamentDayMatchIndex;
  dayBoundsCache: Map<number, DayBoundsCache>;
};

function findDayConstraintsByDayKey(
  constraints: IScheduleConstraints,
  dayKey: number
): IScheduleDayConstraints | null {
  return constraints.days.find((d) => d.dayKey === dayKey) ?? null;
}

type ApplyMatchResult = {
  match: IMatchScheduleItem | null;
  skipReasons: BulkSkipReason[];
};

function getDayBounds(
  ctx: BulkTransformContext,
  day: IScheduleDayConstraints,
  constraints: IScheduleConstraints,
  timeZone: string
): DayBoundsCache {
  const cached = ctx.dayBoundsCache.get(day.dayKey);
  if (cached) return cached;

  const matchesOnDay = ctx.dayIndex.matchesByDayKey.get(day.dayKey) ?? [];
  const { effectiveStartMs } = getEffectiveDayBounds(
    day,
    matchesOnDay,
    constraints,
    timeZone
  );
  const sessionEndMs = getTournamentDaySessionEndMs(
    day,
    constraints,
    timeZone,
    matchesOnDay
  );
  const bounds = { effectiveStartMs, sessionEndMs };
  ctx.dayBoundsCache.set(day.dayKey, bounds);
  return bounds;
}

function invalidateDayBounds(ctx: BulkTransformContext, dayKey: number): void {
  ctx.dayBoundsCache.delete(dayKey);
}

function applyBulkOpsToMatch(
  match: IMatchScheduleItem,
  ops: BulkAdjustOps,
  constraints: IScheduleConstraints,
  timeZone: string,
  ctx: BulkTransformContext,
  classObject?: Class | null
): ApplyMatchResult {
  const skipReasons: BulkSkipReason[] = [];
  const updated: IMatchScheduleItem = { ...match };
  const hasSchedule = Boolean(match.scheduleDate && match.court);

  if (ops.setDurationMinutes != null && Number.isFinite(ops.setDurationMinutes)) {
    updated.duration = ops.setDurationMinutes;
  }

  const wantsTimeShift = ops.shiftMinutes != null && ops.shiftMinutes !== 0;
  const wantsCourtShift = ops.shiftCourtColumns != null && ops.shiftCourtColumns !== 0;

  if ((wantsTimeShift || wantsCourtShift) && !hasSchedule) {
    skipReasons.push('not_scheduled');
    return { match: updated, skipReasons };
  }

  if (wantsTimeShift && match.scheduleDate) {
    const dayKey = ctx.dayIndex.dayKeyByMatchId.get(match.id);
    const day = dayKey != null ? ctx.dayIndex.dayByKey.get(dayKey) ?? null : null;
    if (!day) {
      return { match: null, skipReasons: ['out_of_day_bounds'] };
    }

    const newStartMs = new Date(match.scheduleDate).getTime() + ops.shiftMinutes! * MS_PER_MINUTE;
    const newIso = new Date(newStartMs).toISOString();
    const shiftedMatch = {
      ...updated,
      scheduleDate: newIso,
      startDate: newIso,
    } as IMatchScheduleItem;

    const shiftedDayKey = resolveTournamentDayKeyForMatch(shiftedMatch, constraints, timeZone);
    if (shiftedDayKey == null || shiftedDayKey !== day.dayKey) {
      return { match: null, skipReasons: ['cross_day'] };
    }

    const duration =
      updated.duration ||
      getMatchDurationFromConstraints(updated, constraints, classObject ?? undefined) ||
      30;
    const { effectiveStartMs, sessionEndMs } = getDayBounds(ctx, day, constraints, timeZone);
    const maxStart = sessionEndMs - duration * MS_PER_MINUTE;

    if (newStartMs < effectiveStartMs || newStartMs > maxStart) {
      return { match: null, skipReasons: ['out_of_day_bounds'] };
    }

    if (
      !matchBelongsToTournamentDay(shiftedMatch, day, constraints, timeZone, [shiftedMatch])
    ) {
      return { match: null, skipReasons: ['out_of_day_bounds'] };
    }

    updated.scheduleDate = newIso;
    updated.startDate = newIso;
    Object.assign(updated, clearMatchExplicitEndTimes(updated));

    const shiftedDayKeyForBounds = ctx.dayIndex.dayKeyByMatchId.get(match.id);
    if (shiftedDayKeyForBounds != null) {
      invalidateDayBounds(ctx, shiftedDayKeyForBounds);
    }
  }

  if (wantsCourtShift && match.court && match.scheduleDate) {
    const dayKey = ctx.dayIndex.dayKeyByMatchId.get(match.id);
    const day = dayKey != null ? ctx.dayIndex.dayByKey.get(dayKey) ?? null : null;
    if (!day) {
      return { match: null, skipReasons: ['out_of_courts'] };
    }

    const idx = day.courts.findIndex((c) => c.id === match.court!.id);
    if (idx < 0) {
      return { match: null, skipReasons: ['out_of_courts'] };
    }

    const newIdx = idx + ops.shiftCourtColumns!;
    if (newIdx < 0 || newIdx >= day.courts.length) {
      return { match: null, skipReasons: ['out_of_courts'] };
    }

    updated.court = day.courts[newIdx];
  }

  if (ops.setDurationMinutes != null && Number.isFinite(ops.setDurationMinutes)) {
    const dayKey = ctx.dayIndex.dayKeyByMatchId.get(match.id);
    if (dayKey != null) {
      invalidateDayBounds(ctx, dayKey);
    }
  }

  return { match: updated, skipReasons };
}

function collectCourtOverlaps(
  changedIds: Set<number>,
  scheduledMatches: IMatchScheduleItem[],
  scheduledIndexById: Map<number, number>,
  constraints: IScheduleConstraints,
  classIdToObject: Record<string, Class>
): { matchId: number; messageKey: string }[] {
  const overlaps: { matchId: number; messageKey: string }[] = [];

  for (const matchId of changedIds) {
    const idx = scheduledIndexById.get(matchId);
    const match = idx != null ? scheduledMatches[idx] : undefined;
    if (!match?.scheduleDate || !match.court) continue;

    const classObject =
      classIdToObject[String(match.classId)] ?? classIdToObject[match.classId];
    const duration =
      match.duration ||
      getMatchDurationFromConstraints(match, constraints, classObject) ||
      30;
    const startMs = new Date(match.scheduleDate).getTime();

    if (
      wouldOverlap(
        startMs,
        duration,
        match.court.id,
        [...changedIds],
        scheduledMatches,
        constraints
      )
    ) {
      overlaps.push({ matchId, messageKey: 'overlapError' });
    }
  }

  return overlaps;
}

export function simulateBulkScheduleTransform(
  params: SimulateBulkTransformParams
): BulkTransformResult {
  const {
    schedule,
    unscheduledMatches,
    targetMatchIds,
    ops,
    constraints,
    timeZone,
    classIdToObject = {},
    mode = 'preview',
  } = params;

  const skipped: BulkTransformResult['skipped'] = [];
  const appliedIds = new Set<number>();
  const skippedIds = new Set<number>();
  const effectiveTargetIds: number[] = [];

  for (const id of targetMatchIds) {
    if (effectiveTargetIds.length >= MAX_SCHEDULE_BULK_SELECTION) {
      skipped.push({ matchId: id, reason: 'cap' });
      skippedIds.add(id);
      continue;
    }
    effectiveTargetIds.push(id);
  }

  const scheduledMatches = flattenScheduledMatches(schedule).map((m) => ({ ...m }));
  const nextUnscheduled = unscheduledMatches.map((m) => ({ ...m }));
  const scheduledIndexById = new Map<number, number>();
  scheduledMatches.forEach((m, idx) => scheduledIndexById.set(m.id, idx));

  const unscheduledIndexById = new Map<number, number>();
  nextUnscheduled.forEach((m, idx) => unscheduledIndexById.set(m.id, idx));

  const dayIndex = buildTournamentDayMatchIndex(scheduledMatches, constraints, timeZone);
  const ctx: BulkTransformContext = {
    dayIndex,
    dayBoundsCache: new Map(),
  };
  const changedIds = new Set<number>();

  for (const matchId of effectiveTargetIds) {
    const scheduledIdx = scheduledIndexById.get(matchId);
    const unscheduledIdx = unscheduledIndexById.get(matchId);
    const source =
      scheduledIdx != null
        ? scheduledMatches[scheduledIdx]
        : unscheduledIdx != null
          ? nextUnscheduled[unscheduledIdx]
          : undefined;

    if (!source) continue;

    const classObject =
      classIdToObject[String(source.classId)] ?? classIdToObject[source.classId];
    const { match: transformed, skipReasons } = applyBulkOpsToMatch(
      source,
      ops,
      constraints,
      timeZone,
      ctx,
      classObject
    );

    for (const reason of skipReasons) {
      skipped.push({ matchId, reason });
      skippedIds.add(matchId);
    }

    if (!transformed) continue;

    if (scheduledIdx != null) {
      Object.assign(scheduledMatches[scheduledIdx], transformed);
      changedIds.add(matchId);
      appliedIds.add(matchId);
    } else if (unscheduledIdx != null) {
      Object.assign(nextUnscheduled[unscheduledIdx], transformed);
      appliedIds.add(matchId);
    }
  }

  const courtOverlaps = collectCourtOverlaps(
    changedIds,
    scheduledMatches,
    scheduledIndexById,
    constraints,
    classIdToObject
  );

  let rebuiltSchedule: ITournamentSchedule | undefined;
  let warnings: ScheduleConflict[] = [];

  if (mode === 'apply') {
    rebuiltSchedule = rebuildEntireSchedule(scheduledMatches, constraints, timeZone);
    warnings = detectScheduleConflicts(
      rebuiltSchedule,
      constraints,
      timeZone,
      classIdToObject
    );
  }

  return {
    scheduledMatches,
    unscheduledMatches: nextUnscheduled,
    skipped,
    warnings,
    courtOverlaps,
    rebuiltSchedule,
    stats: {
      appliedCount: appliedIds.size,
      skippedCount: skippedIds.size,
    },
  };
}

export function computeGroupDragOpsFromFreeSlotDrop(
  anchor: { scheduleDate: string; courtId: number },
  targetSlot: IFreeSlotItem,
  constraints: IScheduleConstraints
): BulkAdjustOps | null {
  if (!targetSlot.scheduleDate) return null;

  const day = findDayConstraintsByDayKey(constraints, targetSlot.day);
  if (!day) return null;

  const anchorIdx = day.courts.findIndex((c) => c.id === anchor.courtId);
  const targetIdx = day.courts.findIndex((c) => c.id === targetSlot.court.id);
  if (anchorIdx < 0 || targetIdx < 0) return null;

  const anchorStartMs = new Date(anchor.scheduleDate).getTime();
  const targetStartMs = new Date(targetSlot.scheduleDate).getTime();
  const shiftMinutes = Math.round((targetStartMs - anchorStartMs) / MS_PER_MINUTE);

  return {
    shiftMinutes,
    shiftCourtColumns: targetIdx - anchorIdx,
  };
}

export function hasBulkOpsEffect(ops: BulkAdjustOps): boolean {
  return (
    (ops.shiftMinutes != null && ops.shiftMinutes !== 0) ||
    (ops.shiftCourtColumns != null && ops.shiftCourtColumns !== 0) ||
    (ops.setDurationMinutes != null && Number.isFinite(ops.setDurationMinutes))
  );
}
